// src/pages/ProductList.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams, useOutletContext, useNavigate, useLocation, useParams } from 'react-router-dom'; // Added useParams
import { Helmet } from 'react-helmet-async'; // Import Helmet
import { usePaginatedProducts } from '../hooks/usePaginatedProducts';
import useWorker from '../hooks/useWorker';
import ProductCardSkeleton from '../components/ProductCardSkeleton';
import ProductCard from '../components/ProductCard'; // Use direct import instead of lazy loading
import CategoryFilter from '../components/CategoryFilter'; // Import new CategoryFilter
import SelectedFilters from '../components/SelectedFilters'; // Import SelectedFilters
import ProductListHeader from '../components/ProductListHeader'; // Import ProductListHeader
import Breadcrumb from '../components/Breadcrumb'; // Import Breadcrumb component
import API from '../api'; // Import API
import { FEATURES } from '../config/features';
import { getSearchParam, normalizeCategoryFromURL, parseURLWithDefense, generateProductUrl } from '../utils/urlEncoding';
import { generateCategoryUrl, generateBreadcrumbUrls } from '../utils/enhancedLinkGenerators'; // Import enhanced link generators for SEO-friendly URLs
// Import dynamic URL mapping utilities for automatic category support
import { urlMappingCache, getCategoryFromSlug, getSubcategoryFromSlug } from '../utils/dynamicUrlMapping';
import './ProductList.css';
import SkeletonScreen from '../components/SkeletonScreen';

// Category popularity ranking (most popular first)
const CATEGORY_POPULARITY_RANKING = {
  'Health & Wellness': 1,
  'Beauty': 2,
  'Grocery & Gourmet Foods': 3,
  'Clothing': 4,
  'Home Decor': 5,
  'Beverages': 6,
  'Baby & Kids': 7,
  'Bath & Body': 8,
  'Books & Media': 9,
  'Jewelry': 10,
  'Toys': 11,
  'Pets': 12,
  'Home & Garden': 13,
  'Office & School Supplies': 14,
  'Oral Care': 15,
  'Art & Collectibles': 16,
  'Household Supplies': 17
};

// Helper function to sort categories by popularity
const sortCategoriesByPopularity = (categories) => {
  return categories.sort((a, b) => {
    const rankA = CATEGORY_POPULARITY_RANKING[a] || 999; // Unknown categories go to the end
    const rankB = CATEGORY_POPULARITY_RANKING[b] || 999;
    return rankA - rankB;
  });
};

function ProductList() {
  const navigate = useNavigate();
  const { countryCode, selectedCountries } = useOutletContext();
  const [searchParams] = useSearchParams();
  const location = useLocation(); // Get location object
  const params = useParams(); // Get URL route parameters for clean URLs
  const siteBaseUrl = 'https://www.originselect.com'; // Define base URL

  // Clean URL parameter extraction
  const categorySlug = params.category;
  const subcategorySlug = params.subcategory;
  
  // Convert URL slugs back to category names using dynamic mapping
  const categoryFromSlug = useMemo(() => {
    if (categorySlug) {
      try {
        const category = getCategoryFromSlug(categorySlug);
        console.log('🔍 Category resolved from slug:', { categorySlug, category });
        return category;
      } catch (error) {
        console.warn(`Failed to resolve category slug "${categorySlug}":`, error);
        return null;
      }
    }
    return null;
  }, [categorySlug]);
  
  const subcategoryFromSlug = useMemo(() => {
    if (subcategorySlug) {
      try {
        const subcategory = getSubcategoryFromSlug(subcategorySlug);
        console.log('🔍 Subcategory resolved from slug:', { subcategorySlug, subcategory });
        return subcategory;
      } catch (error) {
        console.warn(`Failed to resolve subcategory slug "${subcategorySlug}":`, error);
        return null;
      }
    }
    return null;
  }, [subcategorySlug]);

  // Use defensive URL parsing to handle malformed URLs from external sources (like Pinterest)
  const defensiveParams = useMemo(() => parseURLWithDefense(location.search), [location.search]);
  
  // Check if this is a reset request (from Shop All button)
  const isResetRequest = getSearchParam(defensiveParams, 'reset') === 'true';
  
  // Check if this is a fresh category selection from home page
  const isFreshCategorySelection = getSearchParam(defensiveParams, 'fresh') === 'true';
  
  // Use the new URL encoding utilities to handle category parameters properly
  // Priority: Clean URL params > Query params > Session storage (if not ignoring)
  const shouldIgnoreStorage = isResetRequest || isFreshCategorySelection;
  
  // Determine initial category: Clean URL slug > Query param > Session storage
  const initialCat = categoryFromSlug || 
    normalizeCategoryFromURL(getSearchParam(defensiveParams, 'category')) || 
    (!shouldIgnoreStorage ? sessionStorage.getItem('selectedCategory') : '') || '';
    
  // Determine initial subcategory: Clean URL slug > Query param > Session storage  
  const initialSub = subcategoryFromSlug ||
    normalizeCategoryFromURL(getSearchParam(defensiveParams, 'subcategory')) || 
    (!shouldIgnoreStorage ? sessionStorage.getItem('selectedSubCategory') : '') || '';

  console.log('🔍 ProductList URL Analysis:', {
    pathname: location.pathname,
    categorySlug,
    subcategorySlug,
    categoryFromSlug,
    subcategoryFromSlug,
    initialCat,
    initialSub,
    isCleanUrl: Boolean(categorySlug),
    queryParams: Object.fromEntries(searchParams.entries())
  });
  
  // Handle multiple subcategories from URL (comma-separated)
  const initialSubCategories = useMemo(() => {
    const urlSubcategories = getSearchParam(defensiveParams, 'subcategories') || getSearchParam(defensiveParams, 'subcategory');
    if (urlSubcategories) {
      return urlSubcategories.split(',').map(s => normalizeCategoryFromURL(s.trim())).filter(Boolean);
    }
    // If reset=true or fresh=true, don't use stored subcategories
    if (shouldIgnoreStorage) {
      return [];
    }
    const storedSubcategories = sessionStorage.getItem('selectedSubCategories');
    if (storedSubcategories) {
      try {
        return JSON.parse(storedSubcategories);
      } catch {
        return [];
      }
    }
    return initialSub ? [initialSub] : [];
  }, [defensiveParams, initialSub, shouldIgnoreStorage]);
  
  // Worker for heavy computations
  const { executeTask: executeWorkerTask } = useWorker('/workers/computation.worker.js');

  // Memoize countryList to ensure stable reference if values don't change
  const countryList = useMemo(() => {
    const urlCountries = getSearchParam(defensiveParams, 'countries');
    const rawCountries = urlCountries || [countryCode, ...selectedCountries].join(',');
    return rawCountries
      .split(',')
      .filter(Boolean);
  }, [getSearchParam(defensiveParams, 'countries'), countryCode, selectedCountries]); // More specific dependency

  // primary vs extra countries
  const selectedCountry = countryCode;
  const multiCountries = countryList.filter(c => c !== selectedCountry);

  // theme + UI state
  const themeClass = `theme-${countryCode}`;
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const infiniteScrollTriggerRef = useRef(null); // Only need this ref for infinite scroll
  const pageContainerRef = useRef(null); // Ensure this ref is applied to the main container div
  
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(initialCat);
  const [selectedSubCategory, setSelectedSubCategory] = useState(initialSub);
  const [selectedSubCategories, setSelectedSubCategories] = useState(initialSubCategories);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false); // Mobile filter overlay state
  const [showScrollToTop, setShowScrollToTop] = useState(false); // Scroll to top button state

  // Function to validate and filter subcategories against current category
  const validateSubcategoriesForCategory = useCallback((category, subcategoriesArray, availableSubcategories) => {
    if (!category || !subcategoriesArray.length || !availableSubcategories.length) {
      return subcategoriesArray;
    }

    // Filter subcategories to only include those that belong to the current category
    const validSubcategories = subcategoriesArray.filter(subcat => 
      availableSubcategories.includes(subcat)
    );

    console.log('🧹 Subcategory validation:', {
      category,
      originalSubcategories: subcategoriesArray,
      availableSubcategories,
      validSubcategories,
      removedCount: subcategoriesArray.length - validSubcategories.length
    });

    return validSubcategories;
  }, []);

  // Additional validation for query parameter mismatches
  const validateQueryParameterConsistency = useCallback(() => {
    const queryCategory = getSearchParam(defensiveParams, 'category');
    const querySubcategories = getSearchParam(defensiveParams, 'subcategories') || getSearchParam(defensiveParams, 'subcategory');
    
    if (queryCategory && querySubcategories && categoryFromSlug && categoryFromSlug !== queryCategory) {
      console.log('⚠️  Detected query parameter mismatch:', {
        urlCategory: categoryFromSlug,
        queryCategory: queryCategory,
        querySubcategories: querySubcategories
      });
      
      // Clear conflicting query subcategories when URL category doesn't match
      return true; // Indicates we should ignore query subcategories
    }
    
    return false;
  }, [defensiveParams, categoryFromSlug]);

  // FIXED: Stabilized URL-state synchronization to prevent infinite loops
  const isInitializedRef = useRef(false);
  const lastUrlParamsRef = useRef('');
  
  // Initialize state from URL parameters when route changes
  useEffect(() => {
    // Create a unique key for the current URL parameters
    const currentUrlKey = JSON.stringify({ 
      categorySlug, 
      subcategorySlug, 
      searchParams: location.search 
    });
    
    // Only initialize if we haven't processed these URL parameters before
    if (!isInitializedRef.current || lastUrlParamsRef.current !== currentUrlKey) {
      // Priority: Clean URL slug > Query param > Session storage
      const categoryFromUrl = categoryFromSlug || normalizeCategoryFromURL(getSearchParam(defensiveParams, 'category'));
      const subCategoryFromUrl = subcategoryFromSlug || normalizeCategoryFromURL(getSearchParam(defensiveParams, 'subcategory'));
      
      // Check if we should ignore storage (fresh category selection or reset)
      const isReset = getSearchParam(defensiveParams, 'reset') === 'true';
      const isFresh = getSearchParam(defensiveParams, 'fresh') === 'true';
      const ignoreStorage = isReset || isFresh;
      
      console.log('🔄 Initializing ProductList state:', {
        categorySlug,
        subcategorySlug,
        categoryFromSlug,
        subcategoryFromSlug,
        categoryFromUrl,
        subCategoryFromUrl,
        isReset,
        isFresh,
        ignoreStorage,
        isFirstTime: !isInitializedRef.current
      });
      
      // Check for query parameter mismatches first
      const hasQueryMismatch = validateQueryParameterConsistency();
      
      // Calculate initial subcategories considering fresh/reset flags and query mismatches
      let initSubCategories = [];
      const urlSubcategories = !hasQueryMismatch ? (getSearchParam(defensiveParams, 'subcategories') || getSearchParam(defensiveParams, 'subcategory')) : null;
      
      if (urlSubcategories) {
        initSubCategories = urlSubcategories.split(',').map(s => normalizeCategoryFromURL(s.trim())).filter(Boolean);
      } else if (!ignoreStorage && !hasQueryMismatch) {
        const storedSubcategories = sessionStorage.getItem('selectedSubCategories');
        if (storedSubcategories) {
          try {
            initSubCategories = JSON.parse(storedSubcategories);
          } catch {
            initSubCategories = [];
          }
        } else if (subCategoryFromUrl) {
          initSubCategories = [subCategoryFromUrl];
        }
      }

      // Validate initial subcategories against the current category if we have subcategory data
      if (categoryFromUrl && initSubCategories.length > 0 && subCategories.length > 0) {
        const validSubcategories = validateSubcategoriesForCategory(categoryFromUrl, initSubCategories, subCategories);
        if (validSubcategories.length !== initSubCategories.length) {
          console.log(`🧹 Initial validation: Removed ${initSubCategories.length - validSubcategories.length} invalid subcategories for category "${categoryFromUrl}"`);
          initSubCategories = validSubcategories;
        }
      }
      
      // Set initial state from URL or sessionStorage (but respect ignoreStorage flag)
      setSelectedCategory(categoryFromUrl || (!ignoreStorage ? sessionStorage.getItem('selectedCategory') : '') || '');
      setSelectedSubCategory(subCategoryFromUrl || (!ignoreStorage ? sessionStorage.getItem('selectedSubCategory') : '') || '');
      setSelectedSubCategories(initSubCategories);
      
      isInitializedRef.current = true;
      lastUrlParamsRef.current = currentUrlKey;
    }
  }, [categoryFromSlug, subcategoryFromSlug, location.search, defensiveParams, subCategories, validateSubcategoriesForCategory, validateQueryParameterConsistency]); // Re-run when URL params change
  
  // Update URL when category or subcategory changes (but only after initialization)
  const urlUpdateTimeoutRef = useRef(null);
  useEffect(() => {
    if (!isInitializedRef.current) return; // Don't update URL during initialization
    
    // Debounce URL updates to prevent rapid successive changes
    if (urlUpdateTimeoutRef.current) {
      clearTimeout(urlUpdateTimeoutRef.current);
    }
    
    urlUpdateTimeoutRef.current = setTimeout(() => {
      const params = new URLSearchParams(location.search);
      let hasChanges = false;
      
      // Handle category parameter
      const currentCategoryParam = params.get('category');
      if (selectedCategory && selectedCategory !== currentCategoryParam) {
        params.set('category', selectedCategory);
        hasChanges = true;
      } else if (!selectedCategory && currentCategoryParam) {
        params.delete('category');
        hasChanges = true;
      }

      // Handle subcategory parameter (support both single and multiple)
      const currentSubcategoryParam = params.get('subcategory');
      const currentSubcategoriesParam = params.get('subcategories');
      
      if (selectedSubCategories.length > 0) {
        // Use multiple subcategories
        params.set('subcategories', selectedSubCategories.join(','));
        if (currentSubcategoryParam) params.delete('subcategory'); // Remove single subcategory param
        hasChanges = true;
      } else if (!selectedSubCategories.length) {
        // Clear both subcategory params
        if (currentSubcategoryParam) {
          params.delete('subcategory');
          hasChanges = true;
        }
        if (currentSubcategoriesParam) {
          params.delete('subcategories');
          hasChanges = true;
        }
      }
      
      // Remove reset parameter if present (cleanup after processing)
      if (params.get('reset')) {
        params.delete('reset');
        hasChanges = true;
      }
      
      // Preserve countries parameter
      const existingCountries = searchParams.get('countries');
      if (existingCountries) {
        params.set('countries', existingCountries);
      } else if (countryList.length > 0) {
        params.set('countries', countryList.join(','));
        hasChanges = true;
      }

      // Only navigate if there are actual changes to prevent infinite loops
      if (hasChanges) {
        const newSearch = params.toString();
        const currentSearch = location.search.substring(1); // Remove leading '?'
        
        if (newSearch !== currentSearch) {
          navigate(`${location.pathname}?${newSearch}`, { replace: true });
        }
      }
    }, 100); // 100ms debounce to prevent rapid updates
    
    return () => {
      if (urlUpdateTimeoutRef.current) {
        clearTimeout(urlUpdateTimeoutRef.current);
      }
    };
  }, [selectedCategory, selectedSubCategory, selectedSubCategories, countryList.join(','), location.pathname]); // Removed useMultiSelect

  // Define V2 endpoint based on filters - Always use category-level endpoint for multi-select
  const endpoint = useMemo(() => {
    // Always use category-level endpoint and filter on frontend for multi-select
    if (selectedCategory) {
      return FEATURES.V2_ENDPOINTS.CATEGORY_COUNTRIES
        ? '/api/v2/products/by-category-and-countries'
        : '/api/products/by-category-and-countries';
    }
    return FEATURES.V2_ENDPOINTS.PRODUCTS 
      ? '/api/v2/products'
      : '/api/products';
  }, [selectedCategory]);

  // Define fallback V1 endpoint for seamless fallback - Always use category-level
  const fallbackEndpoint = useMemo(() => {
    // Always use category-level endpoint for multi-select
    if (selectedCategory) {
      return '/api/products/by-category-and-countries';
    }
    return '/api/products';
  }, [selectedCategory]);

  // Define params based on filters and memoize the params object - Always category-level
  const productParams = useMemo(() => {
    const p = { countries: countryList.join(',') };
    if (selectedCategory) {
      p.category = selectedCategory;
    }
    // Don't include subcategory in API params (always filter on frontend)
    return p;
  }, [countryList, selectedCategory]);

  // Use the new V2 paginated products hook with server-side pagination
  const {
    products: allProducts,
    loading,
    loadingMore,
    error: productsError,
    hasMore: serverHasMore,
    loadMore,
    refetch,
    meta
  } = usePaginatedProducts({
    endpoint: endpoint,
    params: productParams,
    fallbackEndpoint: fallbackEndpoint,
    useCache: true,
    enableInfiniteScroll: true,
    limit: FEATURES.PAGINATION_SETTINGS.DEFAULT_LIMIT
  });

  // FIXED: More selective pagination reset to prevent infinite loops
  const previousEndpointRef = useRef('');
  const previousParamsRef = useRef('');
  
  useEffect(() => {
    if (!isInitializedRef.current) return; // Don't refetch during initialization
    
    const currentEndpoint = endpoint;
    const currentParamsString = JSON.stringify(productParams);
    
    // Only refetch if there's a meaningful change in endpoint or params
    const endpointChanged = currentEndpoint !== previousEndpointRef.current;
    const paramsChanged = currentParamsString !== previousParamsRef.current;
    
    if ((endpointChanged || paramsChanged) && (previousEndpointRef.current || previousParamsRef.current)) {
      refetch(true); // Pass true to clear cache and reset pagination
      window.scrollTo(0, 0);
    }
    
    // Update refs with current values
    previousEndpointRef.current = currentEndpoint;
    previousParamsRef.current = currentParamsString;
  }, [endpoint, productParams, refetch]); // Keep only essential dependencies

  // Handle any errors from the hook
  useEffect(() => {
    if (productsError) {
      setError({
        message: 'Failed to load products. Please try again later.',
        details: productsError.message
      });
    } else {
      setError(null);
    }
  }, [productsError]);
  
  // Apply frontend filtering for multi-select subcategories
  const visibleProducts = useMemo(() => {
    let products = allProducts || [];
    
    // Filter by selected subcategories if any are selected
    if (selectedSubCategories.length > 0) {
      products = products.filter(product => 
        selectedSubCategories.includes(product.subCategory || product.subcategory)
      );
    }
    
    return products;
  }, [allProducts, selectedSubCategories]);
  
  // hasMore comes from the server-side pagination
  const hasMore = useMemo(() => {
    return serverHasMore;
  }, [serverHasMore]);

  // Save filter state to session storage when it changes - Updated for multi-select
  useEffect(() => {
    if (selectedCategory) {
      sessionStorage.setItem('selectedCategory', selectedCategory);
    } else {
      sessionStorage.removeItem('selectedCategory');
    }
    
    if (selectedSubCategory) {
      sessionStorage.setItem('selectedSubCategory', selectedSubCategory);
    } else {
      sessionStorage.removeItem('selectedSubCategory');
    }
    
    if (selectedSubCategories.length > 0) {
      sessionStorage.setItem('selectedSubCategories', JSON.stringify(selectedSubCategories));
    } else {
      sessionStorage.removeItem('selectedSubCategories');
    }
  }, [selectedCategory, selectedSubCategory, selectedSubCategories]);
  
  // FIXED: Simplified scroll restoration to prevent infinite loops and auto-scroll issues
  useEffect(() => {
    // Only restore scroll position on initial mount, not on every product update
    if (allProducts?.length > 0) {
      const savedScrollPosition = sessionStorage.getItem('productListScrollPosition');
      if (savedScrollPosition) {
        const scrollPos = parseInt(savedScrollPosition, 10);
        if (!isNaN(scrollPos) && scrollPos > 0) {
          // Simple scroll restoration without triggering loadMore
          setTimeout(() => {
            window.scrollTo(0, scrollPos);
            // Clear the saved position to prevent repeated restorations
            sessionStorage.removeItem('productListScrollPosition');
          }, 100);
        }
      }
    }
    
    // ADDED: Enable scrolling on body when component mounts - but don't override user actions
    if (document.body.style.overflow === 'hidden') {
      document.body.style.overflow = 'auto';
    }
    if (document.documentElement.style.overflow === 'hidden') {
      document.documentElement.style.overflow = 'auto';
    }
    
    return () => {
      // Don't force removal of overflow styles on unmount
    };
  }, [allProducts?.length > 0]); // Only run when products are first loaded

  // Save scroll position before unmounting
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem('productListScrollPosition', window.scrollY.toString());
    };

    // Save scroll position when navigating within the app
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      // Save position on unmount
      sessionStorage.setItem('productListScrollPosition', window.scrollY.toString());
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Enhanced load categories effect using worker for better performance
  const countryListString = countryList.join(',');
  useEffect(() => {
    let isMounted = true;
    
    const fetchCategories = async () => {
      try {
        // Use caching with sessionStorage for categories
        const cacheKey = `originselect_categories_${countryListString}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        
        if (cachedData) {
          const { categories, timestamp } = JSON.parse(cachedData);
          const now = Date.now();
          // Use cache if it's less than 10 minutes old
          if (now - timestamp < 10 * 60 * 1000) {
            if (isMounted) {
              // Ensure cached categories are sorted by popularity
              const sortedCategories = sortCategoriesByPopularity([...categories]);
              setCategories(sortedCategories);
            }
            return;
          }
        }
        
        // Fetch from API if no valid cache
        const { data } = await API.get(`/api/products/categories?countries=${countryListString}`); // Use API
        
        if (isMounted) {
          const categoryList = data.categories || [];
          
          // Sort categories by popularity ranking
          const sortedCategories = sortCategoriesByPopularity(categoryList);
          
          setCategories(sortedCategories);
          
          // Update URL mapping cache with fresh categories from backend
          // This ensures new categories get proper URL slugs automatically
          try {
            urlMappingCache.updateCache(sortedCategories, []);
            console.log('URL mapping updated with fresh categories from backend');
          } catch (error) {
            console.warn('Failed to update URL mapping with categories:', error);
          }
          
          // Cache the result
          sessionStorage.setItem(cacheKey, JSON.stringify({
            categories: sortedCategories,
            timestamp: Date.now()
          }));
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
        if (isMounted) setCategories([]);
      }
    };

    fetchCategories();
    return () => { isMounted = false };
  }, [countryListString]);

  // load subCategories once a category is chosen - also enhanced with caching
  useEffect(() => {
    let isMounted = true;
    if (!selectedCategory) {
      setSubCategories([]);
      return () => { isMounted = false };
    }

    const fetchSubcategories = async () => {
      try {
        // Use caching with sessionStorage for subcategories
        const cacheKey = `originselect_subcategories_${selectedCategory}_${countryListString}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        
        if (cachedData) {
          const { subcategories, timestamp } = JSON.parse(cachedData);
          const now = Date.now();
          // Use cache if it's less than 10 minutes old
          if (now - timestamp < 10 * 60 * 1000) {
            if (isMounted) setSubCategories(subcategories);
            return;
          }
        }
        
        // Fetch from API if no valid cache
        const { data } = await API.get( // Use API
          `/api/products/subcategories?category=${encodeURIComponent(selectedCategory)}&countries=${countryListString}`
        );

        // normalize payload (array, lowercase or camelCase)
        const subCategoryList = Array.isArray(data)
          ? data
          : data.subcategories    // if server sent "subcategories"
            || data.subCategories  // if server sent "subCategories"
            || [];

        if (isMounted) {
          setSubCategories(subCategoryList);
          
          // Update URL mapping cache with fresh subcategories from backend
          // This ensures new subcategories get proper URL slugs automatically
          try {
            const currentCategories = urlMappingCache.getAllCategories();
            urlMappingCache.updateCache(currentCategories, subCategoryList);
            console.log(`URL mapping updated with ${subCategoryList.length} subcategories for category: ${selectedCategory}`);
          } catch (error) {
            console.warn('Failed to update URL mapping with subcategories:', error);
          }
          
          // Cache the result
          sessionStorage.setItem(cacheKey, JSON.stringify({
            subcategories: subCategoryList,
            timestamp: Date.now()
          }));
        }
      } catch (err) {
        console.error('Error fetching subcategories:', err);
        if (isMounted) {
          setSubCategories([]);
        }
      }
    };

    fetchSubcategories();

    return () => { isMounted = false };
  }, [selectedCategory, countryListString]);

  // Infinite scroll intersection observer for trigger element
  useEffect(() => {
    const currentRef = infiniteScrollTriggerRef.current; // Use infiniteScrollTriggerRef instead
    if (!currentRef || !hasMore || loading || loadingMore) return;
    
    const handleIntersection = (entries) => {
      const entry = entries[0];
      
      // More conservative intersection handling to prevent auto-scroll issues
      if (entry.isIntersecting && !loading && !loadingMore && hasMore) {
        // Add a small delay to prevent rapid-fire loading
        setTimeout(() => {
          if (!loading && !loadingMore && hasMore) {
            loadMore();
          }
        }, 100);
      }
    };
    
    const observer = new IntersectionObserver(handleIntersection, { 
      root: null, // Use viewport as root
      rootMargin: '50px 0px', // Load when trigger is 50px away from viewport
      threshold: 0.1 // Load when 10% of trigger is visible
    });
    
    observer.observe(currentRef);
    
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, [hasMore, loading, loadingMore, loadMore]); // Updated dependencies

  // Updated scroll handler for server-side pagination as backup for intersection observer
  useEffect(() => {
    let scrollTimeout = null;
    
    const handleScroll = () => {
      // Debounce scroll events to prevent excessive calls
      if (scrollTimeout) clearTimeout(scrollTimeout);
      
      scrollTimeout = setTimeout(() => {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        
        // Show scroll-to-top button when user has scrolled down 300px
        setShowScrollToTop(scrollTop > 300);
        
        // Load more products logic
        if (loading || loadingMore || !hasMore) return;
        
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        
        // More conservative threshold: load when user reaches 85% of the page
        if (windowHeight + scrollTop >= documentHeight * 0.85) {
          loadMore();
        }
      }, 150); // 150ms debounce
    };
    
    // Use passive true for better scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasMore, loading, loadingMore, loadMore]);

  // Category filter handlers
  const handleCategoryChange = useCallback((category) => {
    const previousCategory = selectedCategory;
    setSelectedCategory(category);
    
    // Clear subcategories whenever:
    // 1. Category is being deselected (empty category)
    // 2. Category is changing to a different one
    // 3. Any change from the previous category
    if (!category || category !== previousCategory) {
      setSelectedSubCategories([]);
    }
  }, [selectedCategory]);

  const handleSubCategoriesChange = useCallback((subcategories) => {
    setSelectedSubCategories(subcategories);
  }, []);

  const handleApplyFilters = useCallback(() => {
    // Filters are applied automatically through state changes
    // Close mobile filter panel if open
    setIsMobileFilterOpen(false);
  }, []);

  // Scroll to top function
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    // For clean URLs, navigate away from category-specific route
    if (categorySlug) {
      // Navigate to general products page when clearing all filters from clean URL
      navigate(`/country/${countryCode}/products?reset=true`);
    } else {
      // For query-based URLs, just update state
      setSelectedCategory('');
      setSelectedSubCategories([]);
    }
    setIsMobileFilterOpen(false);
  }, [categorySlug, countryCode, navigate]);

  // Remove individual filter handlers for the new SelectedFilters component
  const handleRemoveCategory = useCallback(() => {
    // For clean URLs, navigate away from category-specific route
    if (categorySlug) {
      // Navigate to general products page when removing category from clean URL
      navigate(`/country/${countryCode}/products?reset=true`);
    } else {
      // For query-based URLs, just update state
      setSelectedCategory('');
      setSelectedSubCategories([]); // Clear all subcategories when category is removed
    }
  }, [categorySlug, countryCode, navigate]);

  const handleRemoveSubCategory = useCallback((subCategory) => {
    // For clean URLs with subcategories, we need to handle navigation
    if (subcategorySlug && selectedSubCategories.length === 1) {
      // If removing the only subcategory from a clean URL, navigate back to category-only URL
      const categoryUrl = generateCategoryUrl(countryCode, selectedCategory);
      navigate(categoryUrl);
    } else if (categorySlug && selectedSubCategories.length > 1) {
      // If multiple subcategories, need to rebuild URL with remaining subcategories
      // For now, just remove from state and let the URL update effect handle it
      setSelectedSubCategories(prev => prev.filter(s => s !== subCategory));
    } else {
      // For query-based URLs, just update state
      setSelectedSubCategories(prev => prev.filter(s => s !== subCategory));
    }
  }, [subcategorySlug, categorySlug, selectedSubCategories.length, countryCode, selectedCategory, navigate]);

  const handleProductClick = useCallback((productOrId) => {
    // Save current scroll position
    sessionStorage.setItem('productListScrollPosition', window.scrollY.toString());
    
    // Handle both product object and product ID
    const productId = typeof productOrId === 'string' ? productOrId : productOrId._id;
    const productName = typeof productOrId === 'object' ? productOrId.name : null;
    
    // Navigate to product page using the SEO-friendly URL with product name
    const productUrl = generateProductUrl(countryCode, productId, productName);
    navigate(productUrl);
  }, [navigate, countryCode]);

  // Enhanced SEO meta generation with brand-query optimization
  const generateSEOTitle = (category, country, subcategory = null) => {
    const countryName = country === 'canada' ? 'Canada' : 'USA';
    
    if (subcategory) {
      return `OriginSelect ${subcategory} | ${category} Products ${countryName}`;
    }
    
    if (category) {
      return `OriginSelect ${category} Products | Authentic Local ${category} | ${countryName}`;
    }
    
    return `OriginSelect Products | Quality Local Goods | ${countryName}`;
  };

  const generateSEODescription = (category, country, subcategory = null) => {
    const countryName = country === 'canada' ? 'Canada' : 'USA';
    
    const templates = {
      'Beauty & Personal Care': `Shop OriginSelect ${category.toLowerCase()} products from local artisans in ${countryName}. Search "OriginSelect beauty products" to discover quality skincare, cosmetics, and personal care items made with natural ingredients.`,
      'Health & Wellness': `Find OriginSelect ${category.toLowerCase()} products from trusted local producers in ${countryName}. Search "OriginSelect health products" for natural supplements, organic health foods, and wellness essentials.`,
      'Home Decor': `Browse OriginSelect ${category.toLowerCase()} products from local artisans in ${countryName}. Search "OriginSelect home decor" for handcrafted furniture, decorations, and home accessories.`,
      'Grocery & Gourmet Foods': `Discover OriginSelect ${category.toLowerCase()} products from local producers in ${countryName}. Search "OriginSelect gourmet foods" for organic foods, specialty ingredients, and artisanal treats.`,
      'Clothing': `Explore OriginSelect ${category.toLowerCase()} products from local designers and artisans in ${countryName}. Search "OriginSelect clothing" for sustainable fashion, handmade garments, and unique styles.`,
      'Baby & Kids': `Shop OriginSelect ${category.toLowerCase()} products from trusted local artisans in ${countryName}. Search "OriginSelect baby products" for safe, sustainable, and quality baby essentials made with natural ingredients.`,
      'default': `Find OriginSelect ${category ? category.toLowerCase() : 'local'} products from local artisans and producers in ${countryName}. Search "OriginSelect ${category ? category.toLowerCase() : 'products'}" for quality, handcrafted items with cultural heritage and craftsmanship.`
    };
    
    let description = templates[category] || templates.default;
    
    if (subcategory) {
      description = `Browse OriginSelect ${subcategory.toLowerCase()} products in our ${category.toLowerCase()} collection from ${countryName}. ${description}`;
    }
    
    return description;
  };

  // Generate SEO-friendly canonical URL (clean URL format) - Fixed domain consistency
  const generateCanonicalUrl = () => {
    const baseUrl = 'https://www.originselect.com'; // Fixed: Match sitemap domain version
    
    if (selectedCategory) {
      try {
        return `${baseUrl}${generateCategoryUrl(countryCode, selectedCategory, selectedSubCategory)}`;
      } catch (error) {
        console.warn('Failed to generate clean canonical URL, falling back to current URL:', error);
        return `${baseUrl}${location.pathname}${location.search}`;
      }
    }
    
    return `${baseUrl}/country/${countryCode}/products`;
  };

  // Dynamic page title and description with enhanced SEO
  const pageTitle = generateSEOTitle(selectedCategory, countryCode, selectedSubCategory);
  const metaDescription = generateSEODescription(selectedCategory, countryCode, selectedSubCategory);
  const canonicalUrl = generateCanonicalUrl();

  // Generate SEO-friendly breadcrumbs
  const seoFriendlyBreadcrumbs = useMemo(() => {
    try {
      return generateBreadcrumbUrls(countryCode, selectedCategory, selectedSubCategory);
    } catch (error) {
      console.warn('Failed to generate SEO breadcrumbs, using fallback:', error);
      // Fallback to current breadcrumb structure
      return [
        {
          label: 'Home',
          url: `/country/${countryCode}/home`,
          isActive: false
        },
        {
          label: 'Products',
          url: `/country/${countryCode}/products`,
          isActive: !selectedCategory
        },
        ...(selectedCategory ? [{
          label: selectedCategory,
          url: generateCategoryUrl(countryCode, selectedCategory),
          isActive: !selectedSubCategory
        }] : []),
        ...(selectedSubCategory ? [{
          label: selectedSubCategory,
          url: generateBreadcrumbUrls(countryCode, selectedCategory, selectedSubCategory).subcategory,
          isActive: true
        }] : [])
      ];
    }
  }, [countryCode, selectedCategory, selectedSubCategory]);

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: seoFriendlyBreadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.url
    }))
  };

  // Clear subcategory filters when fresh category selection is detected
  const freshProcessedRef = useRef(false);
  useEffect(() => {
    console.log('PRODUCTLIST: Fresh category check:', isFreshCategorySelection);
    console.log('PRODUCTLIST: Current URL:', location.search);
    console.log('PRODUCTLIST: Fresh already processed:', freshProcessedRef.current);
    
    if (isFreshCategorySelection && !freshProcessedRef.current) {
      console.log('PRODUCTLIST: Clearing subcategory filters from fresh category selection');
      
      // Clear subcategory selections from sessionStorage when coming from home page
      sessionStorage.removeItem('selectedSubCategory');
      sessionStorage.removeItem('selectedSubCategories');
      
      // Reset subcategory state
      setSelectedSubCategory('');
      setSelectedSubCategories([]);
      
      // Remove the 'fresh' parameter from URL to clean it up
      const newParams = new URLSearchParams(location.search);
      newParams.delete('fresh');
      const newUrl = `${location.pathname}?${newParams.toString()}`;
      console.log('PRODUCTLIST: Cleaning URL to:', newUrl);
      window.history.replaceState({}, '', newUrl);
      
      // Mark as processed to prevent running again
      freshProcessedRef.current = true;
    }
  }, [isFreshCategorySelection, location.search, location.pathname]); // Removed selectedSubCategories from dependencies

  // Validate subcategories when category or available subcategories change
  useEffect(() => {
    if (!isInitializedRef.current || !selectedCategory || !subCategories.length) return;

    const validatedSubcategories = validateSubcategoriesForCategory(
      selectedCategory, 
      selectedSubCategories, 
      subCategories
    );

    // Update subcategories if validation removed any invalid ones
    if (validatedSubcategories.length !== selectedSubCategories.length) {
      console.log(`🧹 Removed ${selectedSubCategories.length - validatedSubcategories.length} invalid subcategories for category "${selectedCategory}"`);
      setSelectedSubCategories(validatedSubcategories);
    }
  }, [selectedCategory, subCategories, selectedSubCategories, validateSubcategoriesForCategory]);

  // Handle category changes from navigation (clean up invalid subcategories immediately)
  useEffect(() => {
    if (!selectedCategory) return;

    // When category changes, immediately clear subcategories that will be invalid
    // This prevents showing invalid subcategories even briefly before they get validated
    if (selectedSubCategories.length > 0) {
      // We'll validate against available subcategories once they load
      // For now, just log that we're monitoring this
      console.log('📍 Category changed to:', selectedCategory, 'with subcategories:', selectedSubCategories);
    }
  }, [selectedCategory]);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        
        {/* SEO Meta Tags */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        
        {/* Enhanced Canonical URL - Points to clean URL format */}
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbStructuredData)}
        </script>
        
        {/* Website entity with SearchAction for sitelink search-box */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Website",
            "name": "OriginSelect",
            "url": "https://www.originselect.com",
            "description": "Discover authentic, locally-made products from artisans across Canada and the USA",
            "potentialAction": {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": `https://www.originselect.com/country/${countryCode}/search?q={search_term_string}`
              },
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
        
        {/* SiteNavigationElement for primary navigation */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SiteNavigationElement",
            "name": "Primary Navigation",
            "url": `https://www.originselect.com/country/${countryCode}`,
            "hasPart": [
              {
                "@type": "SiteNavigationElement",
                "name": "Home",
                "url": `https://www.originselect.com/country/${countryCode}/home`
              },
              {
                "@type": "SiteNavigationElement", 
                "name": "Products",
                "url": `https://www.originselect.com/country/${countryCode}/products`
              },
              {
                "@type": "SiteNavigationElement",
                "name": "About",
                "url": `https://www.originselect.com/country/${countryCode}/about`
              },
              {
                "@type": "SiteNavigationElement",
                "name": "Blog", 
                "url": `https://www.originselect.com/country/${countryCode}/blog`
              },
              {
                "@type": "SiteNavigationElement",
                "name": "Favorites",
                "url": `https://www.originselect.com/country/${countryCode}/favorites`
              }
            ]
          })}
        </script>
        
        {/* Category-specific structured data */}
        {selectedCategory && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              "name": `${selectedCategory} Products${selectedSubCategory ? ` - ${selectedSubCategory}` : ''}`,
              "description": metaDescription,
              "url": canonicalUrl,
              "mainEntity": {
                "@type": "ItemList",
                "numberOfItems": visibleProducts.length,
                "itemListElement": visibleProducts.slice(0, 10).map((product, index) => ({
                  "@type": "Product",
                  "position": index + 1,
                  "name": product.name || product.title,
                  "description": product.description,
                  "url": `https://www.originselect.com${generateProductUrl(countryCode, product.id, product.name || product.title)}`,
                  "image": product.image_url || product.imageUrl,
                  "offers": product.price ? {
                    "@type": "Offer",
                    "price": product.price.toString(),
                    "priceCurrency": product.currency || "CAD"
                  } : undefined
                }))
              }
            })}
          </script>
        )}
      </Helmet>
      <div className={`product-list-page ${themeClass}`} ref={pageContainerRef}>
        {/* SEO-Enhanced Breadcrumb Navigation */}
        <Breadcrumb items={seoFriendlyBreadcrumbs.map(breadcrumb => ({
          label: breadcrumb.label,
          href: breadcrumb.url,
          isActive: breadcrumb.isActive
        }))} />
        
        <div className="product-header">
          <h1>Products</h1>
          <p>Discover authentic, locally-made products from across {selectedCountry.charAt(0).toUpperCase() + selectedCountry.slice(1)}</p>
        </div>

        {/* Mobile Filter Button */}
        <div className="mobile-filter-btn-container">
          <button 
            className="mobile-filter-btn"
            onClick={() => setIsMobileFilterOpen(true)}
            aria-label="Open filters"
          >
            <svg className="filter-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
            </svg>
            Filter by Category
          </button>
        </div>

        {/* Main Content Layout */}
        <div className="main-content-layout">
          {/* Sidebar for Desktop */}
          <div className="sidebar">
            <CategoryFilter
              categories={categories}
              subCategories={subCategories}
              selectedCategory={selectedCategory}
              selectedSubCategories={selectedSubCategories}
              onCategoryChange={handleCategoryChange}
              onSubCategoriesChange={handleSubCategoriesChange}
              isLoading={loading}
            />
          </div>

          {/* Mobile Filter Overlay */}
          {isMobileFilterOpen && (
            <div className="mobile-filter-overlay" onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsMobileFilterOpen(false);
              }
            }}>
              <div className="mobile-filter-panel">
                <div className="mobile-filter-header">
                  <h3>Filter by Category</h3>
                  <button 
                    className="close-mobile-filter"
                    onClick={() => setIsMobileFilterOpen(false)}
                    aria-label="Close filters"
                  >
                    ×
                  </button>
                </div>
                <CategoryFilter
                  categories={categories}
                  subCategories={subCategories}
                  selectedCategory={selectedCategory}
                  selectedSubCategories={selectedSubCategories}
                  onCategoryChange={handleCategoryChange}
                  onSubCategoriesChange={handleSubCategoriesChange}
                  isLoading={loading}
                />
              </div>
            </div>
          )}

          {/* Main Products Area */}
          <div className="products-content">
            {/* Selected Filters Pills */}
            <SelectedFilters 
              selectedCategory={selectedCategory}
              selectedSubCategories={selectedSubCategories}
              onRemoveCategory={handleRemoveCategory}
              onRemoveSubCategory={handleRemoveSubCategory}
              onClearAll={handleClearFilters}
            />

            {/* Product List Header with Count and Sort */}
            <ProductListHeader 
              productCount={visibleProducts.length}
              totalCount={meta?.totalCount || 0}
              isLoading={loading}
            />

            {/* Skeleton screen shown during loading */}
            <SkeletonScreen 
              type="product" 
              count={12} 
              isVisible={loading} 
            />

            {/* Main content */}
            <div className={`product-content ${!loading ? 'content-visible' : 'content-hidden'}`}>
              {loading && visibleProducts.length === 0 ? (
                <div className="products-grid">
                  {/* Show skeleton loaders while loading */}
                  {[...Array(12)].map((_, index) => (
                    <ProductCardSkeleton key={index} />
                  ))}
                </div>
              ) : error ? (
                <div className="error-container">
                  <div className="error-message">
                    <h3>Error Loading Products</h3>
                    <p>{error.message}</p>
                    <button
                      className="btn retry-btn"
                      onClick={() => refetch()}
                    >
                      Retry
                    </button>
                  </div>
                </div>
              ) : visibleProducts.length === 0 ? (
                <div className="no-products-message">
                  <h3>No products found</h3>
                  <p>Try adjusting your filters or check back later for new products.</p>
                </div>
              ) : (
                <div className="products-grid">
                  {visibleProducts.map(product => (
                    <ProductCard 
                      key={product._id}
                      product={product}
                      countryCode={countryCode}
                      handleProductClick={handleProductClick}
                    />
                  ))}
                  
                  {/* Infinite Scroll Trigger - Invisible element that triggers loading */}
                  {hasMore && (
                    <div 
                      ref={infiniteScrollTriggerRef} 
                      className="infinite-scroll-trigger" 
                      aria-hidden="true"
                      style={{ 
                        height: '20px',
                        visibility: 'hidden'
                      }}
                    />
                  )}

                  {/* Loading indicator for infinite scroll */}
                  {loadingMore && (
                    <div className="infinite-scroll-loading">
                      <div className="loading-spinner">
                        <div className="spinner-circle"></div>
                      </div>
                      <span className="loading-text">Loading more products...</span>
                    </div>
                  )}

                  {/* End of results indicator */}
                  {!hasMore && visibleProducts.length > 0 && (
                    <div className="end-of-results">
                      <div className="end-indicator">
                        <span className="end-icon">✨</span>
                        <span className="end-text">You've seen all products!</span>
                      </div>
                    </div>
                  )}

                  {/* Debug Info - Only show in development mode with better formatting */}
                  {process.env.NODE_ENV === 'development' && (
                    <div className="pagination-debug-info">
                      <div className="debug-header">
                        <span role="img" aria-label="Bug">🐛</span> Infinite Scroll Debug Info
                      </div>
                      <div className="debug-item">
                        hasMore: <span className={`debug-value-${hasMore}`}>
                          <span role="img" aria-label={hasMore ? "Check" : "Cross"}>{hasMore ? '✅' : '❌'}</span> {hasMore.toString()}
                        </span>
                      </div>
                      <div className="debug-item">
                        serverHasMore: <span className={`debug-value-${serverHasMore}`}>
                          <span role="img" aria-label={serverHasMore ? "Check" : "Cross"}>{serverHasMore ? '✅' : '❌'}</span> {serverHasMore.toString()}
                        </span>
                      </div>
                      <div className="debug-item">
                        loading: <span className={loading ? 'debug-value-loading' : 'debug-value-false'}>
                          <span role="img" aria-label={loading ? "Loading" : "Check"}>{loading ? '🔄' : '✅'}</span> {loading.toString()}
                        </span>
                      </div>
                      <div className="debug-item">
                        loadingMore: <span className={loadingMore ? 'debug-value-loading' : 'debug-value-false'}>
                          <span role="img" aria-label={loadingMore ? "Loading" : "Check"}>{loadingMore ? '🔄' : '✅'}</span> {loadingMore.toString()}
                        </span>
                      </div>
                      <div className="debug-item">
                        products: <strong>{visibleProducts?.length || 0}</strong>
                      </div>
                      <div className="debug-item">
                        endpoint: <code>{endpoint}</code>
                      </div>
                      <div className="debug-item">
                        limit: <strong>{FEATURES.PAGINATION_SETTINGS.DEFAULT_LIMIT}</strong>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Scroll to Top Button */}
        {showScrollToTop && (
          <button 
            className="scroll-to-top-btn"
            onClick={scrollToTop}
            aria-label="Scroll to top"
          >
            <svg 
              className="scroll-arrow"
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              width="24"
              height="24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M5 15l7-7 7 7" 
              />
            </svg>
          </button>
        )}
      </div>
    </>
  );
}

export default ProductList;
